/** English Workforce & Education Translations */
export const workforce = {
  workforce: {
    pageTitle: "Workforce & Education Dimension",
    weightInFII: "weight in YFI",
    heroSubtitle: "Equipping Young People with Skills for Economic Opportunity (18-40 years)",
    quote: "23% of young workers are underemployed relative to their education, yet 85% of future jobs require digital skills. The gap between education and opportunity is not a shortage of learners—it is a shortage of aligned, accessible pathways to quality employment.",
    fiiWeightDistribution: "YFI Weight Distribution",
    mentalHealth: "Mental Health",
    workforceLabel: "Workforce",
    entrepreneurship: "Entrepreneurship",
    understandCrisis: "Understand the crisis",
    crisisTitle: "The Workforce & Education Crisis in Numbers",
    crisisSubtitle: "Critical statistics affecting global youth employability and education",
    crisis: {
      inThree: "in 3",
      stat1Label: "global youth unemployment (18-24 years) vs. 5.8% adult",
      stat2Label: "youth NOT in education, employment, or training (NEET) globally",
      stat3Label: "workers underemployed relative to education",
      stat4Label: "jobs will require digital skills by 2030",
      stat5Label: "global average tertiary education enrollment (range: 15%-78%)",
      stat6Label: "young people in Sub-Saharan Africa outside formal workforce",
      stat7Label: "average daily time on digital skill development",
      stat8Label: "higher unemployment in emerging vs. developed markets",
    },
    severity: {
      crisis: "Crisis",
      severe: "Severe",
      concerning: "Concerning",
    },
    coreProblem: "The Core Problem",
    coreProblemDesc: "Unlike mental health (which varies by individual wellbeing), workforce & education gaps are driven by structural barriers: access to quality training, digital infrastructure availability, job market alignment with skills taught, and employer demand for specific competencies. These are solvable problems if JCI addresses them at scale.",
    fiiTitle: "YFI Workforce Methodology",
    fiiSubtitle: "Workforce Score = 30% of Youth Futures Index",
    subWeight: "sub-weight",
    globalAverage: "Global average",
    highest: "Highest",
    lowest: "Lowest",
    fii: {
      employmentRate: "Youth Employment Rate",
      employmentRateDef: "Percentage of 18-40 in formal employment (excluding precarious work)",
      employmentHighest: "Nordic countries (85-90%)",
      employmentLowest: "Sub-Saharan Africa (45-55%)",
      skillsAlignment: "Skills Alignment",
      skillsAlignmentDef: "Percentage of workers whose skills match job requirements",
      skillsHighest: "Switzerland, Germany (70-75%)",
      skillsLowest: "South Asia (30-35%)",
      educationAccess: "Education Access",
      educationAccessDef: "Combined access to tertiary education and vocational training programs",
      educationHighest: "South Korea, Finland (78%)",
      educationLowest: "Niger, Chad (15%)",
    },
    regionalTitle: "Regional Workforce Scores",
    regionalSubtitle: "YFI Workforce Dimension: Regional Performance",
    regions: {
      europe: "Europe",
      asiaPacific: "Asia and the Pacific",
      americas: "America",
      africaMe: "Africa and the Middle East",
    },
    status: {
      good: "Good",
      moderate: "Moderate",
      atRisk: "At Risk",
    },
    regional: {
      europeDrivers: "Dual training systems, strong vocational education",
      europeOpportunity: "Model for JCI training programs",
      asiaPacificDrivers: "Economic variation: Japan/Korea high, South Asia low",
      asiaPacificOpportunity: "Digital literacy and tech skills partnerships",
      americasDrivers: "North America strong, Latin America has gaps",
      americasOpportunity: "JCI + CYE program scalability",
      africaMeDrivers: "Young population, infrastructure shortage",
      africaMeOpportunity: "Highest potential impact—mobile-first training",
    },
    employment: "Employment",
    skillsLabel: "Skills",
    education: "Education",
    projectionTitle: "2024 vs 2040 Projection",
    skillsTitle: "Global Skills Landscape",
    skillsSubtitle: "Skills supply vs demand gap analysis",
    hardSkills: "Hard Skills",
    softSkills: "Soft Skills",
    supply: "Supply",
    demand: "Demand",
    skillNames: {
      dataAnalytics: "Data Analytics",
      cloudComputing: "Cloud Computing",
      aiMl: "AI/Machine Learning",
      cybersecurity: "Cybersecurity",
      projectMgmt: "Project Management",
      criticalThinking: "Critical Thinking",
      communication: "Communication",
      adaptability: "Adaptability",
      leadership: "Leadership",
      creativity: "Creativity",
    },
    deepDiveTitle: "Regional Deep Dives",
    deepDiveSubtitle: "In-depth workforce and education analysis by JCI region",
    strengths: "Strengths",
    challenges: "Challenges",
    jciOpportunity: "JCI Opportunity",
    deepDive: {
      europe: {
        strength1: "Dual education system (Germany, Austria, Switzerland)",
        strength2: "High R&D and innovation investment",
        strength3: "Strong labor protection and benefits",
        challenge1: "Aging workforce",
        challenge2: "Digital skills gaps in some regions",
        challenge3: "Youth unemployment in Southern Europe",
        opportunity: "Export dual training models to other regions",
      },
      asiaPacific: {
        strength1: "High cultural value on education",
        strength2: "Rapid technology adoption",
        strength3: "Young and growing workforce",
        challenge1: "Access inequality between countries",
        challenge2: "Education-market mismatch",
        challenge3: "High competition for formal jobs",
        opportunity: "Mobile-first digital skills programs",
      },
      americas: {
        strength1: "Strong innovation ecosystem (USA, Canada)",
        strength2: "Growing entrepreneurial spirit",
        strength3: "Talent diversity",
        challenge1: "Unequal access to quality education",
        challenge2: "High cost of higher education",
        challenge3: "Graduate underemployment",
        opportunity: "Scale JIB + CYE programs in Latin America",
      },
      africaMe: {
        strength1: "World's youngest population",
        strength2: "High entrepreneurial aspiration",
        strength3: "Growing digital ecosystem",
        challenge1: "Limited educational infrastructure",
        challenge2: "High youth unemployment (14-16%)",
        challenge3: "Critical funding gap",
        opportunity: "Mobile-first training and microfinance for youth",
      },
    },
    initiativesTitle: "JCI Current Initiatives",
    initiativesSubtitle: "JCI workforce and education programs",
    program: "Program",
    reach: "Reach",
    target: "Target",
    statusLabel: "Status",
    initiatives: {
      jibName: "JCI Junior in Business (JIB)",
      jibTarget: "Young entrepreneurs (18-30)",
      jibDesc: "Business literacy training and startup launch",
      cyeName: "Creative Young Entrepreneurship (CYE)",
      cyeTarget: "Creative youth (16-25)",
      cyeDesc: "Creative skills and entrepreneurship program",
      digitalName: "JCI Digital Academy",
      digitalTarget: "All youth (18-40)",
      digitalDesc: "Digital skills training and tech literacy",
      mentoringName: "JCI Mentoring Network",
      mentoringTarget: "Young professionals (22-35)",
      mentoringDesc: "Mentor-mentee connection for career development",
      riseName: "RISE Projects",
      riseTarget: "At-risk communities",
      riseDesc: "Employment-focused community impact projects",
      statusActive: "Active",
      statusGrowing: "Growing",
      statusExpanding: "Expanding",
    },
    outcomesTitle: "Outcomes & Case Studies",
    outcomesSubtitle: "Measurable impact from JCI workforce programs",
    cases: {
      paraguayCountry: "Paraguay",
      paraguayTitle: "JCI Asunción Digital Skills",
      paraguayOutcome: "Digital training program for unemployed youth resulted in high employability rate",
      paraguayMetric: "employability rate",
      syriaCountry: "Syria (Refugees)",
      syriaTitle: "JCI Refugee Skills Initiative",
      syriaOutcome: "Vocational training for Syrian refugee youth in neighboring countries",
      syriaMetric: "youth trained",
      boliviaCountry: "Bolivia",
      boliviaTitle: "JCI Santa Cruz Emprende",
      boliviaOutcome: "Micro-entrepreneurship program for rural community youth",
      boliviaMetric: "businesses launched",
    },
    actionsTitle: "Recommended Actions for JCI",
    actionsSubtitle: "Strategic priorities to close the workforce gap",
    actions: {
      action1Title: "Scale Digital Academy",
      action1Desc: "Expand digital skills training to all regions, prioritizing Africa/ME",
      action1Effort: "12-18 months",
      action1Impact: "3x reach",
      action2Title: "Corporate Partnerships",
      action2Desc: "Develop employer partnerships to secure employment pathways",
      action2Effort: "6-12 months",
      action2Impact: "Employability +40%",
      action3Title: "Vocational Education",
      action3Desc: "Create market-aligned vocational training programs",
      action3Effort: "18-24 months",
      action3Impact: "Sustainable model",
      action4Title: "Mentoring at Scale",
      action4Desc: "Expand mentoring network to 50,000+ active connections",
      action4Effort: "24-36 months",
      action4Impact: "Career development",
      action5Title: "Recognized Certifications",
      action5Desc: "Develop JCI certifications recognized by global employers",
      action5Effort: "18-24 months",
      action5Impact: "Global credibility",
      action6Title: "Microfinance Fund",
      action6Desc: "Create microfinance fund for young entrepreneurs in emerging markets",
      action6Effort: "12-18 months",
      action6Impact: "Capital access",
    },
    ctaTitle: "Workforce is the Enabler",
    ctaDescription: "Workforce & education connects mental health (foundation) to entrepreneurship (accelerator). Employed young people have better mental health, more confidence, and capacity to start businesses.",
    exploreFII: "Explore YFI",
    exploreMentalHealth: "Explore Mental Health",
    dataSources: { title: "Data Sources" },
  },
} as const;

export type WorkforceTranslations = typeof workforce;
